import { useState, useEffect, useCallback } from 'react';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmt = n => Number(n || 0).toLocaleString('uz-UZ');
const fmtDate = d => d ? new Date(d).toLocaleString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

function PeriodPicker({ from, to, onChange }) {
  const { t } = useLang();
  const presets = [
    { label: t('today'),      days: 0 },
    { label: t('sevenDays'),  days: 7 },
    { label: t('thirtyDays'), days: 30 },
    { label: t('thisMonth'),  month: true },
  ];
  function apply(p) {
    const now = new Date();
    if (p.month) {
      onChange(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10), now.toISOString().slice(0, 10));
    } else if (p.days === 0) {
      onChange(now.toISOString().slice(0, 10), now.toISOString().slice(0, 10));
    } else {
      onChange(new Date(now - p.days * 86400000).toISOString().slice(0, 10), now.toISOString().slice(0, 10));
    }
  }
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {presets.map(p => (
        <button key={p.label} onClick={() => apply(p)} style={{
          padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
          background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)'
        }}>{p.label}</button>
      ))}
      <input type="date" value={from} onChange={e => onChange(e.target.value, to)} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 6, padding: '4px 8px', fontSize: 12 }} />
      <span style={{ color: 'var(--text3)' }}>—</span>
      <input type="date" value={to} onChange={e => onChange(from, e.target.value)} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 6, padding: '4px 8px', fontSize: 12 }} />
    </div>
  );
}

function StatusBadge({ status }) {
  const { t } = useLang();
  const map = {
    sent:    { tKey: 'fiscalSent',    color: 'var(--green)',  bg: 'rgba(0,200,100,0.12)' },
    pending: { tKey: 'fiscalPending', color: '#f5a623',       bg: 'rgba(245,166,35,0.12)' },
    failed:  { tKey: 'fiscalFailed',  color: 'var(--red)',    bg: 'rgba(255,59,92,0.12)' },
  };
  const s = map[status] || { tKey: null, color: 'var(--text3)', bg: 'transparent' };
  return <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{s.tKey ? t(s.tKey) : status}</span>;
}

function ReceiptsTab({ labs }) {
  const { t } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const [to, setTo]     = useState(today);
  const [status, setStatus] = useState('');
  const [data, setData]   = useState({ receipts: [], stats: {} });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ from, to });
    if (status) p.append('status', status);
    const r = await fetch(`${API}/fiscal/receipts?${p}`, { headers: headers() });
    const d = await r.json();
    setData(d);
    setLoading(false);
  }, [from, to, status]);

  useEffect(() => { load(); }, [load]);

  async function resend(id) {
    await fetch(`${API}/fiscal/receipts/${id}/resend`, { method: 'POST', headers: headers() });
    load();
  }

  const s = data.stats || {};
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <PeriodPicker from={from} to={to} onChange={(f, tv) => { setFrom(f); setTo(tv); }} />
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 6, padding: '5px 10px', fontSize: 12 }}>
          <option value="">{t('fiscalAllStatuses')}</option>
          <option value="sent">{t('fiscalSent')}</option>
          <option value="pending">{t('fiscalPending')}</option>
          <option value="failed">{t('fiscalFailed')}</option>
        </select>
        <button onClick={() => setShowModal(true)} style={{ marginLeft: 'auto', padding: '6px 16px', background: 'var(--cyan)', color: '#000', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
          {t('fiscalNewReceipt')}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: t('fiscalStatTotal'),  value: s.total_receipts || data.total || 0 },
          { label: t('fiscalStatAmount'), value: `${fmt(s.total_amount)} ${t('currency')}` },
          { label: t('fiscalSent'),       value: s.sent_count || 0, color: 'var(--green)' },
          { label: t('fiscalPending'),    value: s.pending_count || 0, color: '#f5a623' },
          { label: t('fiscalFailed'),     value: s.failed_count || 0, color: 'var(--red)' },
        ].map(c => (
          <div key={c.label} style={{ background: 'var(--bg2)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: c.color || 'var(--cyan)' }}>{c.value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ color: 'var(--text3)', textAlign: 'center', padding: 40 }}>{t('loading')}</div>
      ) : (
        <div style={{ background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {[t('fiscalColNumber'), t('fiscalStatAmount'), t('fiscalColVat'), t('colStatus'), t('fiscalColDate'), t('colActions')].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.receipts || []).map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: 'var(--cyan)', fontSize: 12 }}>{r.receipt_number}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{fmt(r.amount)} {t('currency')}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text3)' }}>{fmt(r.vat_amount)} {t('currency')}</td>
                  <td style={{ padding: '10px 14px' }}><StatusBadge status={r.status} /></td>
                  <td style={{ padding: '10px 14px', color: 'var(--text3)', fontSize: 12 }}>{fmtDate(r.created_at)}</td>
                  <td style={{ padding: '10px 14px' }}>
                    {r.status !== 'sent' && (
                      <button onClick={() => resend(r.id)} style={{ padding: '3px 10px', borderRadius: 6, background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)', color: 'var(--cyan)', cursor: 'pointer', fontSize: 11 }}>
                        {t('fiscalResend')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!data.receipts?.length && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}>{t('fiscalNoneFound')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <NewReceiptModal labs={labs} onClose={() => { setShowModal(false); load(); }} />}
    </div>
  );
}

function NewReceiptModal({ labs, onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({ lab_id: labs[0]?.id || '', amount: '', item_name: t('fiscalDefaultItem') });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  async function submit() {
    if (!form.lab_id || !form.amount) { setError(t('fiscalFormRequired')); return; }
    setLoading(true);
    const r = await fetch(`${API}/fiscal/receipts/generate`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({
        lab_id: parseInt(form.lab_id),
        amount: parseFloat(form.amount),
        items: [{ name: form.item_name || t('fiscalDefaultItem'), price: parseFloat(form.amount), qty: 1 }]
      })
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error); setLoading(false); return; }
    onClose();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: 28, width: 380, border: '1px solid var(--border)' }}>
        <h3 style={{ margin: '0 0 20px', color: 'var(--cyan)' }}>{t('fiscalNewReceiptTitle')}</h3>
        <select value={form.lab_id} onChange={e => setForm(p => ({ ...p, lab_id: e.target.value }))}
          style={{ width: '100%', marginBottom: 12, padding: '8px 10px', background: '#1a1a2e', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 8 }}>
          {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <input placeholder={`${t('fiscalAmountPlh')} (${t('currency')})`} type="number" value={form.amount}
          onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
          style={{ width: '100%', marginBottom: 12, padding: '8px 10px', background: '#1a1a2e', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 8, boxSizing: 'border-box' }} />
        <input placeholder={t('fiscalItemNamePlh')} value={form.item_name}
          onChange={e => setForm(p => ({ ...p, item_name: e.target.value }))}
          style={{ width: '100%', marginBottom: 12, padding: '8px 10px', background: '#1a1a2e', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 8, boxSizing: 'border-box' }} />
        {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 8, cursor: 'pointer' }}>{t('cancelShort')}</button>
          <button onClick={submit} disabled={loading} style={{ flex: 1, padding: 10, background: 'var(--cyan)', color: '#000', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
            {loading ? '...' : t('fiscalCreateBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SettingsTab({ labs }) {
  const { t } = useLang();
  const [settings, setSettings] = useState({});
  const [saved, setSaved] = useState({});

  useEffect(() => {
    labs.forEach(async l => {
      const r = await fetch(`${API}/fiscal/settings/${l.id}`, { headers: headers() });
      const d = await r.json();
      setSettings(prev => ({ ...prev, [l.id]: d }));
    });
  }, [labs]);

  function update(labId, field, val) {
    setSettings(prev => ({ ...prev, [labId]: { ...prev[labId], [field]: val } }));
  }

  async function save(labId) {
    const s = settings[labId] || {};
    await fetch(`${API}/fiscal/settings/${labId}`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ is_enabled: s.is_enabled, kkm_id: s.kkm_id })
    });
    setSaved(prev => ({ ...prev, [labId]: true }));
    setTimeout(() => setSaved(prev => ({ ...prev, [labId]: false })), 2000);
  }

  return (
    <div>
      <div style={{ background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#f5a623' }}>
        ⚠️ {t('fiscalMockWarning')}
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {labs.map(l => {
          const s = settings[l.id] || {};
          return (
            <div key={l.id} style={{ background: 'var(--bg2)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{l.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                    {t('fiscalModeLabel')} <span style={{ color: s.kkm_id ? 'var(--green)' : '#f5a623' }}>{s.kkm_id ? t('fiscalRealMode') : t('fiscalMockMode')}</span>
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>{t('fiscalFiscalActive')}</span>
                  <div onClick={() => update(l.id, 'is_enabled', !s.is_enabled)}
                    style={{ width: 40, height: 22, borderRadius: 11, background: s.is_enabled ? 'var(--cyan)' : 'var(--border)', position: 'relative', cursor: 'pointer', transition: '0.2s' }}>
                    <div style={{ position: 'absolute', top: 3, left: s.is_enabled ? 20 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: '0.2s' }} />
                  </div>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>{t('fiscalKkmIdLabel')}</label>
                  <input
                    placeholder="KKMSOL-XXXXXXXX"
                    value={s.kkm_id || ''}
                    onChange={e => update(l.id, 'kkm_id', e.target.value)}
                    style={{ width: '100%', padding: '7px 10px', background: '#1a1a2e', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button onClick={() => save(l.id)}
                    style={{ width: '100%', padding: '8px', background: saved[l.id] ? 'rgba(0,200,100,0.15)' : 'rgba(0,212,255,0.12)', border: `1px solid ${saved[l.id] ? 'var(--green)' : 'var(--cyan)'}`, color: saved[l.id] ? 'var(--green)' : 'var(--cyan)', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                    {saved[l.id] ? t('savedMark') : t('save')}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatsTab() {
  const { t } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [to, setTo]     = useState(today);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    const p = new URLSearchParams({ from, to });
    const r = await fetch(`${API}/fiscal/stats?${p}`, { headers: headers() });
    setStats(await r.json());
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const maxCount = stats?.daily ? Math.max(...stats.daily.map(d => parseInt(d.count) || 0), 1) : 1;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <PeriodPicker from={from} to={to} onChange={(f, tv) => { setFrom(f); setTo(tv); }} />
      </div>

      {stats && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 28 }}>
            {[
              { label: t('fiscalStatTotal'),  value: parseInt(stats.total_receipts) },
              { label: t('fiscalStatAmount'), value: `${fmt(stats.total_amount)} ${t('currency')}` },
              { label: t('fiscalStatVat'),    value: `${fmt(stats.total_vat)} ${t('currency')}` },
              { label: t('fiscalSent'),       value: parseInt(stats.sent_count), color: 'var(--green)' },
              { label: t('fiscalPending'),    value: parseInt(stats.pending_count), color: '#f5a623' },
            ].map(c => (
              <div key={c.label} style={{ background: 'var(--bg2)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{c.label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: c.color || 'var(--cyan)' }}>{c.value}</div>
              </div>
            ))}
          </div>

          {stats.daily?.length > 0 && (
            <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>{t('fiscalDailyChart')}</div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 120, overflowX: 'auto', paddingBottom: 8 }}>
                {stats.daily.map(d => (
                  <div key={d.date} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 32 }}>
                    <div title={`${d.count} — ${fmt(d.amount)} ${t('currency')}`}
                      style={{ width: 24, background: 'var(--cyan)', borderRadius: '4px 4px 0 0', height: `${Math.max(4, (parseInt(d.count) / maxCount) * 80)}px`, opacity: 0.8 }} />
                    <div style={{ fontSize: 9, color: 'var(--text3)', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>{d.date?.slice(5)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Fiscal() {
  const { labs } = useLab();
  const { t } = useLang();
  const [tab, setTab] = useState(0);

  const tabs = [t('tabReceipts'), t('tabKkmSettings'), t('tabFiscalStats')];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>{t('fiscalTitle')}</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text3)', fontSize: 13 }}>{t('fiscalSubtitle')}</p>
        </div>
        <span style={{ background: 'rgba(245,166,35,0.12)', color: '#f5a623', border: '1px solid rgba(245,166,35,0.3)', padding: '4px 12px', borderRadius: 20, fontSize: 12 }}>
          {t('fiscalMockBadge')}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
        {tabs.map((tabLabel, i) => (
          <button key={i} onClick={() => setTab(i)} style={{
            padding: '8px 18px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13,
            color: tab === i ? 'var(--cyan)' : 'var(--text3)',
            borderBottom: tab === i ? '2px solid var(--cyan)' : '2px solid transparent',
            marginBottom: -1,
          }}>{tabLabel}</button>
        ))}
      </div>

      {tab === 0 && <ReceiptsTab labs={labs} />}
      {tab === 1 && <SettingsTab labs={labs} />}
      {tab === 2 && <StatsTab />}
    </div>
  );
}
